import { Error } from './redux/reducers/error';
import {
  EventCode,
  ElementType,
  PaymentMethodBasicInfo,
  Intent,
  PaymentMethodWithRedirect,
  PaymentConsentResponse,
} from '@airwallex/airwallex-payment-elements';
import axios from 'axios';
import queryString from 'query-string';
import { DccData } from '~models/dccElement';
import { getCheckoutApiUrl, clientRetrieveItem } from '~components/utils';

export const DEFAULT_STATUS_INVALID = 'state_invalid_for_operation';
export interface NextAction {
  url?: string;
  data?: {
    jwt: string;
    stage: string;
    version?: string;
  };
  type?: string;
  dcc_data?: DccData;
}

const ThreeDsElementList = ['threeDsFrictionless', 'threeDsChallenge'] as const;
export type ExtendElementType = ElementType | typeof ThreeDsElementList[number] | 'hpp' | 'redirectPage';
const ExtendEventList = [
  'onResize',
  'onRedirect',
  'onComplete',
  'onDynamicCurrencyConversion',
  'onChallenge',
  'onChallengeSuccess',
  'onChallengeError',
  'onIntentRetrieved',
  'onIntentRetrievedError',
  'onPaymentMethodCreated',
  'onPaymentMethodCreatedError',
  'onLogRequest',
  'onLogResponse',
  'onSwitchMethod',
  'onClickConfirmButton',
] as const;
export type ExtendEventCode = EventCode | typeof ExtendEventList[number];

export type ExtendEventDetail = {
  type: ExtendElementType;
  complete?: boolean;
  empty?: boolean;
  brand?: string;
  intent?: Intent;
  paymentConsent?: PaymentConsentResponse;
  error?: Error;
  code: string;
  redirectUrl?: string;
  cardBin?: string;
  status?: string;
  auto_capture?: boolean;
  paymentMethod?: PaymentMethodBasicInfo;
  next_action?: NextAction;
  width?: number;
  height?: number;
  cvcLength?: number;
  redirectPayload?: {
    method: PaymentMethodWithRedirect;
    redirectUrl: string;
  };
  path?: string;
  req?: object;
  res?: object;
  header?: object;
  method?: ExtendElementType;
};

export const sendEvent = (data: ExtendEventDetail, origin: string | undefined) => {
  if (origin) {
    const { error, ...restData } = data;
    const errorData = {
      code: error?.code || DEFAULT_STATUS_INVALID,
      message: error?.message || DEFAULT_STATUS_INVALID,
      original_code: error?.provider_original_response_code,
    };
    const newData = {
      ...restData,
      error: error?.message && errorData,
    };

    if (origin === 'null') {
      // Support React Native solution
      origin = '*';
    }

    window.parent.postMessage(newData, origin);
    try {
      // Fire tracking event
      const { intent, next_action, ...restTrackData } = restData || {};
      let url = `${getCheckoutApiUrl()}/collect?origin=${encodeURIComponent(origin)}&${queryString.stringify(
        restTrackData,
      )}&error=${encodeURIComponent(JSON.stringify(error || {}))}`;
      if (intent) {
        url += `&intent_id=${intent?.id}&status=${intent?.status || ''}`;
      }
      if (next_action) {
        url += `&next_action_type=${next_action?.type}&next_action_url=${encodeURIComponent(next_action?.url || '')}`;
      }
      axios
        .get(url, {
          withCredentials: false,
          headers: {
            'Content-Type': 'application/json',
            'Awx-Tracker': clientRetrieveItem('awx_tracker'),
          },
        })
        .catch(() => console.info(`${data?.type} collect event failed.`));
    } catch {
      console.info(`${data?.type} collect event failed.`);
    }
  }
};

export const RECEIVE_EVENTS = {
  // common
  update: 'update',
  confirm: 'confirm',
  focus: 'focus',
  blur: 'blur',
  clear: 'clear',
  // card number element
  createPaymentMethod: 'createPaymentMethod',
  getIntent: 'getIntent',
  update_expiry: 'update_expiry',
  update_cvc: 'update_cvc',
  // message from threeDsFrictionless and threeDsChallenge to notify element who triggered 3ds
  on3dsSuccess: 'on3dsSuccess',
  on3dsError: 'on3dsError',
  on3dsChallengeError: 'on3dsChallengeError',
  // create payment consent
  createPaymentConsent: 'createPaymentConsent',
};

export const POST_EVENTS: { [key: string]: ExtendEventCode } = {
  // common
  onReady: 'onReady',
  onSubmit: 'onSubmit',
  onSuccess: 'onSuccess',
  onError: 'onError',
  onChange: 'onChange',
  onResize: 'onResize',
  onBlur: 'onBlur',
  onFocus: 'onFocus',
  onRedirect: 'onRedirect',
  onComplete: 'onComplete',
  onClickConfirmButton: 'onClickConfirmButton',
  // Dcc
  onDynamicCurrencyConversion: 'onDynamicCurrencyConversion',
  // 3ds frictionless and 3ds challenge
  onChallenge: 'onChallenge',
  onChallengeSuccess: 'onChallengeSuccess',
  onChallengeError: 'onChallengeError',
  // card number element
  onIntentRetrieved: 'onIntentRetrieved',
  onIntentRetrievedError: 'onIntentRetrievedError',
  onPaymentMethodCreated: 'onPaymentMethodCreated',
  onPaymentMethodCreatedError: 'onPaymentMethodCreatedError',
  // log request and response
  onLogRequest: 'onLogRequest',
  onLogResponse: 'onLogResponse',
  // hpp switch payment method
  onSwitchMethod: 'onSwitchMethod',
};

export const SUCCESS_STATUSES = ['SUCCEEDED', 'REQUIRES_CAPTURE', 'AUTHORIZED', 'VERIFIED'];
export const FAIL_STATUSES = ['CANCELLED'];
